import React, { useState, useEffect, useRef } from 'react';
import { getIconsData } from '../services/api';
import './IconDropdown.css';
import { matchesSearch } from '../utils/search';

const IconDropdown = ({ value, onChange, placeholder = "Select icon..." }) => {
  const [icons, setIcons] = useState([]);
  const [filteredIcons, setFilteredIcons] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch icons on component mount
  useEffect(() => {
    const fetchIcons = async () => {
      setLoading(true);
      try {
        const response = await getIconsData();
        const iconsData = response.data || {};
        
        // Convert object to array of {key, value} pairs
        const iconsArray = Object.entries(iconsData).map(([key, value]) => ({
          key,
          value
        }));
        
        setIcons(iconsArray);
        setFilteredIcons(iconsArray);
      } catch (error) {
        console.error('Error fetching icons:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIcons();
  }, []);

  // Filter icons based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredIcons(icons);
    } else {
      const filtered = icons.filter(icon => 
        matchesSearch(searchTerm, icon.key, icon.value)
      );
      setFilteredIcons(filtered);
    }
  }, [searchTerm, icons]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (icon) => {
    onChange(icon.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value) return '';
    const selectedIcon = icons.find(icon => icon.value === value);
    return selectedIcon ? `${selectedIcon.key} (${selectedIcon.value})` : value;
  };

  return (
    <div className="icon-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="icon-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="icon-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading icons...</div>
          ) : filteredIcons.length === 0 ? (
            <div className="dropdown-no-results">No icons found</div>
          ) : (
            filteredIcons.map((icon) => (
              <div
                key={icon.key}
                className="dropdown-option"
                onClick={() => handleSelect(icon)}
              >
                <div className="icon-key">{icon.key}</div>
                <div className="icon-value">{icon.value}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default IconDropdown;
